import { useState } from "react";
import { Button } from "../../../components/ui/button";
import { Eye, Edit, Tag } from "lucide-react";

const TAG_OPTIONS = ["urgent", "high_risk", "VIP", "follow_up"];
const TAG_COLORS = {
  urgent: "bg-red-100 text-red-700",
  high_risk: "bg-amber-100 text-amber-700",
  VIP: "bg-purple-100 text-purple-700",
  follow_up: "bg-blue-100 text-blue-700",
};

const formatStatus = (s) =>
  (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const TagDropdown = ({ currentTags, onToggle }) => {
  const [open, setOpen] = useState(false);
  const available = TAG_OPTIONS.filter((t) => !(currentTags || []).includes(t));
  if (available.length === 0) return null;
  return (
    <div className="relative">
      <button
        className="w-5 h-5 flex items-center justify-center rounded bg-slate-100 hover:bg-slate-200 text-slate-400"
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        data-testid="add-tag-btn"
      >
        <Tag className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute top-6 left-0 z-20 bg-white border border-slate-200 shadow-lg rounded p-1 min-w-[100px]">
          {available.map((t) => (
            <button
              key={t}
              className="block w-full text-left px-2 py-1 text-xs hover:bg-slate-50 rounded"
              onClick={(e) => {
                e.stopPropagation();
                onToggle(t);
                setOpen(false);
              }}
            >
              + {t}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const TableView = ({ quotes, getStatusColor, onView, onEdit, onToggleTag }) => (
  <div className="bg-white border border-slate-200 overflow-hidden" data-testid="table-view">
    {quotes.length === 0 ? (
      <div className="p-8 text-center text-slate-500">No quotes found</div>
    ) : (
      <>
        <div className="sm:hidden px-3 py-1.5 bg-slate-50 text-xs text-slate-400 border-b border-slate-200">
          Scroll right to see all columns →
        </div>
        <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Customer</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Route</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Move Date</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Price</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Margin</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Tags</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {quotes.map((quote) => (
              <tr key={quote.quote_id} className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div className="font-medium text-[#0F172A] text-sm">{quote.customer?.name || "N/A"}</div>
                  <div className="text-xs text-slate-400">{quote.customer?.email}</div>
                </td>
                <td className="px-4 py-3 text-sm">
                  {quote.from_postcode} → {quote.to_postcode}
                  <div className="text-xs text-slate-400">{quote.bedrooms} bed {quote.property_type}</div>
                </td>
                <td className="px-4 py-3 text-sm">{quote.move_date}</td>
                <td className="px-4 py-3">
                  {(quote.final_price || quote.customer_price || quote.estimated_price) ? (
                    <>
                      <div className="font-medium text-[#FF5A1F] text-sm tabular-nums">
                        £{(quote.final_price || quote.customer_price || quote.estimated_price).toFixed(2)}
                      </div>
                      <div className="text-xs text-slate-400 tabular-nums">Cost: £{(quote.internal_cost || 0).toFixed(2)}</div>
                    </>
                  ) : (
                    <span className="text-slate-300">&mdash;</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className="text-green-600 font-medium text-sm">{quote.margin_percent?.toFixed(0) || 0}%</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(quote.tags || []).map((t) => (
                      <span
                        key={t}
                        className={`text-[10px] px-1.5 py-0.5 rounded font-medium cursor-pointer ${TAG_COLORS[t] || "bg-slate-100"}`}
                        onClick={() => onToggleTag(quote.quote_id, quote.tags, t)}
                      >
                        {t} ×
                      </span>
                    ))}
                    <TagDropdown
                      currentTags={quote.tags || []}
                      onToggle={(tag) => onToggleTag(quote.quote_id, quote.tags, tag)}
                    />
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(quote.status)}`}>
                    {formatStatus(quote.status)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => onView(quote)} data-testid={`view-quote-${quote.quote_id}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => onEdit(quote)} data-testid={`edit-quote-${quote.quote_id}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>
    )}
  </div>
);
